import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaUser, FaEnvelope, FaGithub, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { db } from '../../firebase';
import { collection, getDocs, setDoc, doc } from 'firebase/firestore';

const ProfileManager = () => {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    bio: '',
    tagline: '',
    github: '',
    linkedin: '',
    twitter: '',
    resume: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const profileSnapshot = await getDocs(collection(db, 'profile'));
      if (!profileSnapshot.empty) {
        const profileData = profileSnapshot.docs[0].data();
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, 'profile', 'main'), {
        ...profile,
        updatedAt: new Date().toISOString()
      });
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#1d1836] rounded-2xl p-8 border border-purple-500/20">
      <h2 className="text-3xl font-bold text-white mb-6">Profile Information</h2>
      
      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <FaUser className="inline mr-2" />
              Full Name
            </label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
              placeholder="Your Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <FaEnvelope className="inline mr-2" />
              Email Address
            </label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
              placeholder="your.email@example.com"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Tagline
          </label>
          <input
            type="text"
            value={profile.tagline}
            onChange={(e) => setProfile({ ...profile, tagline: e.target.value })}
            className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
            placeholder="Full Stack Developer | AI Enthusiast"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Bio
          </label>
          <textarea
            value={profile.bio}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500 h-32"
            placeholder="Tell us about yourself..."
          />
        </div>

        <div className="border-t border-purple-500/20 pt-6">
          <h3 className="text-xl font-bold text-white mb-4">Social Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FaGithub className="inline mr-2" />
                GitHub
              </label>
              <input
                type="url"
                value={profile.github}
                onChange={(e) => setProfile({ ...profile, github: e.target.value })}
                className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                placeholder="https://github.com/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FaLinkedin className="inline mr-2" />
                LinkedIn
              </label>
              <input
                type="url"
                value={profile.linkedin}
                onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
                className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FaTwitter className="inline mr-2" />
                Twitter
              </label>
              <input
                type="url"
                value={profile.twitter}
                onChange={(e) => setProfile({ ...profile, twitter: e.target.value })}
                className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                placeholder="https://twitter.com/username"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Resume URL
          </label>
          <input
            type="url"
            value={profile.resume}
            onChange={(e) => setProfile({ ...profile, resume: e.target.value })}
            className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
            placeholder="https://example.com/resume.pdf"
          />
        </div>

        <motion.button
          type="submit"
          disabled={saving}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium shadow-lg disabled:opacity-50"
        >
          <FaSave />
          <span>{saving ? 'Saving...' : 'Save Profile'}</span>
        </motion.button>
      </form>
    </div>
  );
};

export default ProfileManager;
